import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ConvertDialog from './ConvertDialog';
import EmailDialog from './EmailDialog';
import PersonEditorDialog from './PersonEditorDialog';
import { useLead } from './useLead';
import {
  buysProperty,
  formatAddress,
  leadTypeLabel,
  personDisplayName,
  primaryValue,
  propertyOrAreasSummary,
  relationshipLabel,
  sellsProperty,
  sourceLabel,
} from '../../utils/crm';
import {
  ACTIVITY_TYPES,
  LOST_REASONS,
  dayPhrase,
  describeEvent,
  formatDateTime,
  localDateString,
  lostReasonLabel,
  sideNoun,
  stageLabel,
  stagesFor,
  statusLabel,
  taskTypeLabel,
} from '../../utils/crmPipeline';

const SIDE_LABEL = { BUY: 'Buying', SELL: 'Selling' };
const SYSTEM_TYPES = ['STAGE_CHANGED', 'STATUS_CHANGED', 'CONVERTED', 'CAMPAIGN_INQUIRY'];
const ACTIVITY_SHOWN = 5;

const statusClass = (status) => ({ OPEN: 'crm-st-open', NURTURING: 'crm-st-nurture', LOST: 'crm-st-lost', WON: 'crm-st-won' }[status] || '');

/** One open task: tick it to finish it, Skip to set it aside, ⋯ to reschedule, rename or remove. */
const TaskItem = ({ task, finishing, busy, today, onDone, onAct }) => {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [title, setTitle] = useState(task.title || '');
  const [due, setDue] = useState(task.dueDate);
  const overdue = task.dueDate < today;

  const save = async () => {
    if (await onAct(task, { title: title.trim(), dueDate: due })) { setEditing(false); setMenu(false); }
  };

  return (
    <li className={`crm-lm-task ${finishing ? 'crm-lm-task-done' : ''}`}>
      <label className="crm-lm-check">
        <input type="checkbox" checked={finishing} disabled={busy && !finishing} aria-label={`Mark done: ${task.title}`} onChange={() => onDone(task)} />
        <span className="crm-lm-task-title">{task.decryptionFailed ? 'Details unavailable' : task.title}</span>
      </label>
      <span className="crm-stage-tag crm-tag-quiet">{taskTypeLabel(task.type)}</span>
      <span className={`crm-lm-due ${overdue && !finishing ? 'crm-overdue' : ''}`}>{dayPhrase(task.dueDate, today)}</span>
      <span className="crm-lm-task-actions">
        <button type="button" className="crm-link crm-task-link" disabled={busy} onClick={() => onAct(task, { status: 'SKIPPED' })}>Skip</button>
        <button type="button" className="crm-lm-more" aria-label={`More for ${task.title}`} aria-expanded={menu} onClick={() => { setMenu(!menu); setConfirmRemove(false); }}>⋯</button>
      </span>
      {menu && !editing && (
        <div className="crm-lm-task-menu">
          {confirmRemove ? (
            <>
              <span>Remove this task for good?</span>
              <button type="button" className="crm-btn crm-btn-danger" disabled={busy} onClick={() => onAct(task, null)}>Remove</button>
              <button type="button" className="crm-btn" onClick={() => setConfirmRemove(false)}>Keep</button>
            </>
          ) : (
            <>
              <button type="button" className="crm-btn" onClick={() => setEditing(true)}>Rename or reschedule</button>
              <button type="button" className="crm-btn" onClick={() => setConfirmRemove(true)}>Remove…</button>
            </>
          )}
        </div>
      )}
      {editing && (
        <div className="crm-lm-task-menu">
          <input className="crm-input" aria-label="Task title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          <input className="crm-input" type="date" aria-label="Task due date" value={due} onChange={(e) => setDue(e.target.value)} />
          <button type="button" className="crm-btn crm-btn-primary" disabled={busy || !title.trim() || !due} onClick={save}>Save</button>
          <button type="button" className="crm-btn" onClick={() => { setEditing(false); setMenu(false); }}>Cancel</button>
        </div>
      )}
    </li>
  );
};

/**
 * A lead on one screen, opened over the list or board it was clicked from. Everything is one click away:
 * tick a task to finish it, click a stage to move the lead, use the quick row to log a call, and the buttons
 * at the top to convert, pause or close it out. No tabs and no separate pages.
 */
const LeadModal = ({ book, ownerUserId, personId, onClose, onChanged, onOpenTransaction }) => {
  const {
    person, timeline, tasks, status, error, setError, busy, load, changeLead, actOnTask, addTask, addEntry,
  } = useLead({ book, ownerUserId, personId, onChanged });

  const [sidePick, setSidePick] = useState('');
  const [finishing, setFinishing] = useState([]);        // tasks ticked and being saved: shown done at once
  const [showDone, setShowDone] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mode, setMode] = useState('');                    // '' | 'nurture' | 'lost'
  const [pauseUntil, setPauseUntil] = useState('');
  const [reason, setReason] = useState('');
  const [entry, setEntry] = useState({ type: 'CALL', text: '', followUp: '', side: '' });
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', dueDate: localDateString() });
  const [editing, setEditing] = useState(false);
  const [converting, setConverting] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const today = localDateString();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !editing && !converting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, editing, converting]);

  const sides = person ? ['BUY', 'SELL'].filter((s) => person[s === 'BUY' ? 'buyerLead' : 'sellerLead']) : [];
  const side = sides.includes(sidePick) ? sidePick : sides[0];
  const lead = side ? person[side === 'BUY' ? 'buyerLead' : 'sellerLead'] : null;
  const name = person ? personDisplayName(person) : 'Loading…';

  const sideTasks = useMemo(() => tasks.filter((t) => !side || t.side === side), [tasks, side]);
  const allOpen = sideTasks.filter((t) => t.status === 'OPEN');
  const openTasks = allOpen.filter((t) => !finishing.includes(t.id));
  const closedTasks = sideTasks.filter((t) => t.status !== 'OPEN');
  const dueNow = openTasks.filter((t) => t.dueDate <= today);
  const active = lead && (lead.status === 'OPEN' || lead.status === 'NURTURING');
  const isLost = sides.length > 0 && sides.every((s) => person[s === 'BUY' ? 'buyerLead' : 'sellerLead'].status === 'LOST');

  // One click to finish: the task shows as done at once and is saved in the background.
  const finish = async (task) => {
    setFinishing((f) => [...f, task.id]);
    await actOnTask(task, { status: 'DONE' });
    setFinishing((f) => f.filter((id) => id !== task.id));
  };
  const finishAllDue = async () => {
    const batch = dueNow;
    setFinishing((f) => [...f, ...batch.map((t) => t.id)]);
    for (const t of batch) {
      // eslint-disable-next-line no-await-in-loop
      await actOnTask(t, { status: 'DONE' });
    }
    setFinishing((f) => f.filter((id) => !batch.some((t) => t.id === id)));
  };

  const apply = async (body) => {
    if (await changeLead(side, body)) { setMode(''); setMoreOpen(false); setPauseUntil(''); setReason(''); return true; }
    return false;
  };

  const submitEntry = async (event) => {
    event.preventDefault();
    if (await addEntry({ ...entry, side: sides.length > 1 ? side : '' })) {
      setEntry({ type: entry.type, text: '', followUp: '', side: '' });
      setShowFollowUp(false);
    }
  };
  const submitTask = async (event) => {
    event.preventDefault();
    if (await addTask({ title: newTask.title, type: 'TASK', dueDate: newTask.dueDate, side })) {
      setNewTask({ title: '', dueDate: localDateString() });
    }
  };

  const activity = timeline.filter((a) => !a.side || !side || a.side === side);
  const shownActivity = showAllActivity ? activity : activity.slice(0, ACTIVITY_SHOWN);

  const body = () => {
    if (status === 'error') return <p className="crm-error" role="alert">{error}</p>;
    if (!person) return <p className="crm-empty">Loading…</p>;
    if (person.decryptionFailed) return <p className="crm-note">This person&apos;s details could not be read, so nothing can be shown or changed here.</p>;
    return (
      <>
        {error && <p className="crm-error" role="alert">{error}</p>}

        {sides.length > 1 && (
          <div className="crm-segmented" role="tablist" aria-label="Buying or selling">
            {sides.map((x) => (
              <button key={x} type="button" role="tab" aria-selected={side === x} className={`crm-seg ${side === x ? 'active' : ''}`} onClick={() => setSidePick(x)}>
                {SIDE_LABEL[x]}
              </button>
            ))}
          </div>
        )}
        {sides.length === 0 && <p className="crm-note">Not a buyer or seller lead, so there is no pipeline. You can still keep notes below.</p>}

        {lead && (
          <>
            {lead.status === 'WON' && (
              <div className="crm-lm-banner crm-lm-banner-won">
                Converted to a transaction.
                {lead.transactionId && <button type="button" className="crm-link" onClick={() => onOpenTransaction(lead.transactionId)}>Open transaction</button>}
              </div>
            )}
            {lead.status === 'LOST' && (
              <div className="crm-lm-banner crm-lm-banner-lost">
                Lost: {lostReasonLabel(lead.lostReason)}.
                <button type="button" className="crm-link" disabled={busy} onClick={() => apply({ status: 'OPEN' })}>Reopen</button>
              </div>
            )}
            {lead.status === 'NURTURING' && (
              <div className="crm-lm-banner">
                Paused{lead.nextFollowUpDate ? ` until ${dayPhrase(lead.nextFollowUpDate, today)}` : ''}. Its default tasks are on hold.
                <button type="button" className="crm-link" disabled={busy} onClick={() => apply({ status: 'OPEN' })}>Resume</button>
              </div>
            )}

            {lead.status !== 'WON' && (
              <ol className="crm-stepper" aria-label={`${sideNoun(side)} stage`}>
                {stagesFor(side).map((s, i, all) => {
                  const current = s === lead.stage;
                  const passed = i < all.indexOf(lead.stage);
                  return (
                    <li key={s}>
                      <button
                        type="button"
                        className={`crm-step ${current ? 'current' : ''} ${passed ? 'passed' : ''}`}
                        aria-current={current ? 'step' : undefined}
                        disabled={busy || lead.status === 'LOST' || current}
                        onClick={() => apply({ stage: s })}
                      >
                        {stageLabel(s)}
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}

            {active && (
              <section className="crm-lm-section" aria-label="To do">
                <div className="crm-lm-section-head">
                  <h3>To do {openTasks.length > 0 && <span className="crm-count">{openTasks.length}</span>}</h3>
                  {dueNow.length > 1 && (
                    <button type="button" className="crm-link" disabled={busy} onClick={finishAllDue}>Mark {dueNow.length} due tasks done</button>
                  )}
                </div>
                {allOpen.length === 0 && <p className="crm-note">Nothing to do. Add a task below.</p>}
                <ul className="crm-lm-tasks">
                  {allOpen.map((t) => (
                    <TaskItem key={t.id} task={t} finishing={finishing.includes(t.id)} busy={busy} today={today} onDone={finish} onAct={actOnTask} />
                  ))}
                </ul>
                <form className="crm-lm-add" onSubmit={submitTask}>
                  <input className="crm-input" aria-label="New task title" placeholder="Add a task…" maxLength={200} value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                  <input className="crm-input crm-lm-date" type="date" aria-label="New task due date" value={newTask.dueDate} onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })} />
                  <button type="submit" className="crm-btn" disabled={busy || !newTask.title.trim()}>Add</button>
                </form>
                {closedTasks.length > 0 && (
                  <>
                    <button type="button" className="crm-link" onClick={() => setShowDone(!showDone)}>{showDone ? 'Hide' : 'Show'} finished and skipped ({closedTasks.length})</button>
                    {showDone && (
                      <ul className="crm-lm-tasks crm-lm-closed">
                        {closedTasks.map((t) => (
                          <li key={t.id}>
                            <span className="crm-lm-task-title">{t.title}</span>
                            <span className="crm-stage-tag crm-tag-quiet">{t.status === 'DONE' ? 'Done' : 'Skipped'}</span>
                            <button type="button" className="crm-link crm-task-link" disabled={busy} onClick={() => actOnTask(t, { status: 'OPEN' })}>Undo</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </section>
            )}
          </>
        )}

        <section className="crm-lm-section" aria-label="Log activity">
          <div className="crm-lm-section-head"><h3>Log what happened</h3></div>
          <form className="crm-lm-log" onSubmit={submitEntry}>
            <div className="crm-lm-chips" role="radiogroup" aria-label="Type of activity">
              {ACTIVITY_TYPES.map((t) => (
                <button key={t.value} type="button" role="radio" aria-checked={entry.type === t.value}
                  className={`crm-chip ${entry.type === t.value ? 'active' : ''}`} onClick={() => setEntry({ ...entry, type: t.value })}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="crm-lm-add">
              <input className="crm-input" aria-label="Details" placeholder={entry.type === 'NOTE' ? 'Write a note…' : 'What was said? (optional)'} maxLength={4000}
                value={entry.text} onChange={(e) => setEntry({ ...entry, text: e.target.value })} />
              <button type="submit" className="crm-btn crm-btn-primary" disabled={busy}>Log {entry.type === 'NOTE' ? 'note' : (ACTIVITY_TYPES.find((t) => t.value === entry.type)?.label || '').toLowerCase()}</button>
            </div>
            {sides.length > 0 && (showFollowUp ? (
              <label className="crm-lm-followup">Follow up on
                <input className="crm-input" type="date" aria-label="Follow-up after this entry" min={today} value={entry.followUp} onChange={(e) => setEntry({ ...entry, followUp: e.target.value })} />
              </label>
            ) : (
              <button type="button" className="crm-link" onClick={() => setShowFollowUp(true)}>+ Set a follow-up date</button>
            ))}
          </form>
          {activity.length > 0 && (
            <ul className="crm-lm-activity">
              {shownActivity.map((a) => (
                <li key={a.id} className={SYSTEM_TYPES.includes(a.type) ? 'crm-entry-system' : ''}>
                  <span className="crm-entry-when">{formatDateTime(a.occurredAt)}</span>{' '}
                  {SYSTEM_TYPES.includes(a.type) ? describeEvent(a) : (
                    <>
                      <strong>{ACTIVITY_TYPES.find((t) => t.value === a.type)?.label || a.type}</strong>
                      {a.decryptionFailed ? <em> — details unavailable</em> : a.text && <span className="crm-preline">: {a.text}</span>}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {activity.length > ACTIVITY_SHOWN && (
            <button type="button" className="crm-link" onClick={() => setShowAllActivity(!showAllActivity)}>
              {showAllActivity ? 'Show fewer' : `Show all ${activity.length}`}
            </button>
          )}
        </section>

        <section className="crm-lm-section" aria-label="Details">
          <button type="button" className="crm-lm-toggle" aria-expanded={showDetails} onClick={() => setShowDetails(!showDetails)}>
            Details {showDetails ? '▾' : '▸'}
          </button>
          {showDetails && (
            <dl className="crm-facts">
              {(person.emails || []).length > 0 && (<><dt>Email</dt><dd>{person.emails.map((e) => e.value).join(', ')}</dd></>)}
              {(person.phones || []).length > 0 && (<><dt>Phone</dt><dd>{person.phones.map((p) => p.value).join(', ')}</dd></>)}
              <dt>Relationship</dt><dd>{relationshipLabel(person.relationship)}</dd>
              <dt>Source</dt><dd>{sourceLabel(person.source)}</dd>
              {sellsProperty(person.leadType) && formatAddress(person.propertyAddress) && (<><dt>Property to sell</dt><dd>{formatAddress(person.propertyAddress)}</dd></>)}
              {buysProperty(person.leadType) && (person.areasOfInterest || []).length > 0 && (<><dt>Looking in</dt><dd>{person.areasOfInterest.join(', ')}</dd></>)}
              {(person.tags || []).length > 0 && (<><dt>Tags</dt><dd>{person.tags.join(', ')}</dd></>)}
              {person.notes && (<><dt>Notes</dt><dd className="crm-preline">{person.notes}</dd></>)}
            </dl>
          )}
        </section>
      </>
    );
  };

  return createPortal(
    <div className="crm-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crm-lead-modal" role="dialog" aria-modal="true" aria-label={`Lead: ${name}`}>
        <header className="crm-lm-head">
          <div className="crm-lm-title">
            <h2>{name}</h2>
            <div className="crm-lm-sub">
              {person && !person.decryptionFailed && (
                <>
                  <span>{primaryValue(person.emails) || primaryValue(person.phones) || propertyOrAreasSummary(person)}</span>
                  {sides.map((s) => (
                    <span key={s} className={`crm-lm-badge ${statusClass(person[s === 'BUY' ? 'buyerLead' : 'sellerLead'].status)}`}>
                      {sides.length > 1 ? `${SIDE_LABEL[s]}: ` : ''}{statusLabel(person[s === 'BUY' ? 'buyerLead' : 'sellerLead'].status)}
                    </span>
                  ))}
                  {sides.length === 0 && <span className="crm-lm-badge">{relationshipLabel(person.relationship)}</span>}
                  {person.leadType && <span className="crm-lm-type">{leadTypeLabel(person.leadType)}</span>}
                </>
              )}
            </div>
          </div>
          <div className="crm-lm-head-actions">
            {person && !person.decryptionFailed && active && book === 'personal' && (
              <button type="button" className="crm-btn crm-btn-primary" disabled={busy} onClick={() => setConverting(side)}>Convert to transaction</button>
            )}
            {person && !person.decryptionFailed && primaryValue(person.emails) && !isLost && (
              <button type="button" className="crm-btn" onClick={() => setEmailing(true)}>Email</button>
            )}
            {person && !person.decryptionFailed && <button type="button" className="crm-btn" onClick={() => setEditing(true)}>Edit</button>}
            {lead && lead.status === 'OPEN' && (
              <div className="crm-lm-menu">
                <button type="button" className="crm-btn" aria-haspopup="true" aria-expanded={moreOpen} onClick={() => { setMoreOpen(!moreOpen); setMode(''); }}>More ▾</button>
                {moreOpen && (
                  <div className="crm-lm-popover" role="menu">
                    {mode === '' && (
                      <>
                        <button type="button" role="menuitem" onClick={() => setMode('nurture')}>Pause until later…</button>
                        <button type="button" role="menuitem" onClick={() => setMode('lost')}>Mark lost…</button>
                      </>
                    )}
                    {mode === 'nurture' && (
                      <div className="crm-lm-pop-form">
                        <label>Follow up on
                          <input className="crm-input" type="date" aria-label="Pause until" min={today} value={pauseUntil} onChange={(e) => setPauseUntil(e.target.value)} />
                        </label>
                        <button type="button" className="crm-btn crm-btn-primary" disabled={busy || !pauseUntil} onClick={() => apply({ status: 'NURTURING', nextFollowUpDate: pauseUntil })}>Pause</button>
                      </div>
                    )}
                    {mode === 'lost' && (
                      <div className="crm-lm-pop-form">
                        <label>Why was it lost?
                          <select className="crm-select" aria-label="Reason lost" value={reason} onChange={(e) => setReason(e.target.value)}>
                            <option value="">Choose…</option>
                            {LOST_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </label>
                        <button type="button" className="crm-btn crm-btn-danger" disabled={busy || !reason} onClick={() => apply({ status: 'LOST', lostReason: reason })}>Mark lost</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <button type="button" className="crm-lm-close" aria-label="Close" onClick={onClose}>×</button>
          </div>
        </header>
        <div className="crm-lm-body">{body()}</div>
      </div>

      {editing && person && (
        <PersonEditorDialog book={book} ownerUserId={ownerUserId} person={person} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} />
      )}
      {emailing && person && (
        <EmailDialog
          book={book}
          ownerUserId={ownerUserId}
          person={person}
          side={sides.length === 1 ? sides[0] : undefined}
          onClose={() => { setEmailing(false); load(); }}
          onSent={() => { setError(''); onChanged(); }}
        />
      )}
      {converting && person && (
        <ConvertDialog
          book={book}
          ownerUserId={ownerUserId}
          person={person}
          side={converting}
          onClose={() => { setConverting(null); load(); }}
          onOpenTransaction={onOpenTransaction}
          onConverted={() => { setError(''); load(); onChanged(); }}
        />
      )}
    </div>,
    document.body
  );
};

export default LeadModal;
